<template>
    <div
        class="mobile-field-card"
        :class="{
            'completed': isCompleted,
            'current-user': isCurrentUser,
            'readonly': !isCurrentUser
        }"
        @click="handleClick"
    >
        <!-- Field Icon -->
        <div class="field-icon" :class="`icon-${fieldType}`">
            <i :class="fieldTypeIcon"></i>
            <transition name="checkmark">
                <span v-if="isCompleted" class="check-overlay">
                    <i class="fa fa-check"></i>
                </span>
            </transition>
        </div>

        <!-- Field Content -->
        <div class="field-content">
            <h4 class="field-name">{{ fieldLabel }}</h4>
            <div class="field-meta">
                <span
                    class="signee-badge"
                    :class="`badge-${signeeType}`"
                >
                    {{ formatSigneeType(signeeType) }}
                </span>
                <span v-if="isRequired" class="required-indicator">Required</span>
                <span v-if="isCurrentUser && !isCompleted" class="your-turn">Your turn</span>
            </div>
        </div>

        <!-- Action Area -->
        <div class="field-action">
            <span v-if="isCompleted" class="completed-label">Done</span>
            <span v-else-if="isCurrentUser" class="action-cta">
                {{ fieldType === 'signature' ? 'Tap to sign' : 'Tap to fill' }}
                <i class="fa fa-chevron-right"></i>
            </span>
            <span v-else class="waiting-label">
                <i class="fa fa-clock"></i>
                Waiting
            </span>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MobileFieldCard',
    props: {
        field: {
            type: Object,
            required: true
        },
        isCompleted: {
            type: Boolean,
            default: false
        },
        isCurrentUser: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        fieldType() {
            const annotation = this.field?.annotations?.[0];
            return annotation?.type || this.field?.field_type || 'text';
        },

        fieldLabel() {
            return this.field?.field_label || this.field?.label || this.field?.field_name || this.field?.name || 'Field';
        },

        signeeType() {
            const annotation = this.field?.annotations?.find(a => a.signee_type);
            return annotation?.signee_type || this.field?.assigned_to?.role || 'unassigned';
        },

        isRequired() {
            return this.field?.required || false;
        },

        fieldTypeIcon() {
            const icons = {
                signature: 'fa fa-pen-fancy',
                text: 'fa fa-font',
                textarea: 'fa fa-align-left',
                email: 'fa fa-envelope',
                phone: 'fa fa-phone',
                number: 'fa fa-hashtag',
                date: 'fa fa-calendar',
                select: 'fa fa-list',
                dropdown: 'fa fa-caret-down',
                checkbox: 'fa fa-check-square',
                radio: 'fa fa-dot-circle'
            };
            return icons[this.fieldType] || 'fa fa-edit';
        }
    },
    methods: {
        handleClick() {
            if (this.isCompleted) {
                // Could emit a different event to view the completed field
                return;
            }
            this.$emit('click', this.field);
        },

        formatSigneeType(type) {
            if (!type || type === 'unassigned') return 'Unassigned';
            const labels = {
                student: 'Student',
                guardian: 'Guardian',
                instructor: 'Instructor',
                staff: 'Staff',
                admin: 'Admin'
            };
            return labels[type] || type;
        }
    }
};
</script>

<style scoped>
.mobile-field-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background-color: var(--ds-signing-card, #FFFFFF);
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 80px;
    margin-bottom: 2em;
}

.mobile-field-card:active {
    transform: scale(0.98);
}

.mobile-field-card.current-user:not(.completed) {
    border: 2px solid var(--ds-signing-primary, #2563EB);
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.15);
}

.mobile-field-card.completed {
    opacity: 0.8;
    cursor: default;
}

.mobile-field-card.completed:active {
    transform: none;
}

.mobile-field-card.readonly:not(.completed) {
    opacity: 0.7;
    cursor: default;
}

/* Field Icon */
.field-icon {
    position: relative;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 20px;
}

.field-icon.icon-signature {
    background-color: #FEF3C7;
    color: #D97706;
}

.field-icon.icon-text,
.field-icon.icon-textarea {
    background-color: #E0E7FF;
    color: #4F46E5;
}

.field-icon.icon-email {
    background-color: #CFFAFE;
    color: #0891B2;
}

.field-icon.icon-phone {
    background-color: #D1FAE5;
    color: #059669;
}

.field-icon.icon-date {
    background-color: #FCE7F3;
    color: #DB2777;
}

.field-icon.icon-number {
    background-color: #FEF9C3;
    color: #CA8A04;
}

.field-icon.icon-select,
.field-icon.icon-dropdown {
    background-color: #EDE9FE;
    color: #7C3AED;
}

.field-icon.icon-checkbox,
.field-icon.icon-radio {
    background-color: #F3E8FF;
    color: #9333EA;
}

.check-overlay {
    position: absolute;
    top: -4px;
    right: -4px;
    width: 22px;
    height: 22px;
    background-color: var(--ds-signing-success, #059669);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 11px;
    border: 2px solid var(--ds-signing-card, #FFFFFF);
}

/* Checkmark Animation */
.checkmark-enter-active {
    animation: checkmark-pop 0.4s ease;
}

@keyframes checkmark-pop {
    0% {
        transform: scale(0);
    }
    50% {
        transform: scale(1.3);
    }
    100% {
        transform: scale(1);
    }
}

/* Field Content */
.field-content {
    flex: 1;
    min-width: 0;
}

.field-name {
    margin: 0 0 6px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--ds-signing-text, #1E293B);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.field-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.signee-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    color: white;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge-student {
    background-color: #3B82F6;
}

.badge-guardian {
    background-color: #10B981;
}

.badge-instructor {
    background-color: #F59E0B;
}

.badge-staff {
    background-color: #8B5CF6;
}

.badge-admin {
    background-color: #EF4444;
}

.badge-unassigned {
    background-color: #6B7280;
}

.required-indicator {
    font-size: 11px;
    font-weight: 600;
    color: #DC2626;
}

.your-turn {
    font-size: 11px;
    font-weight: 600;
    color: var(--ds-signing-primary, #2563EB);
}

/* Action Area */
.field-action {
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.completed-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--ds-signing-success, #059669);
}

.action-cta {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--ds-signing-primary, #2563EB);
}

.action-cta i {
    font-size: 12px;
}

.waiting-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--ds-signing-muted, #64748B);
}

.waiting-label i {
    font-size: 14px;
}
</style>
